import {
  BaseTelemetryEvent,
  PropertyCheckEventProperties,
  TelemetryEventType,
  TelemetryPropertyValueType,
} from './types';

export interface ErrorEvent extends BaseTelemetryEvent {
  readonly type: TelemetryEventType.Error;
  readonly message: string;
  readonly stack: string;
  readonly properties: Record<string | keyof ErrorEventProperties, TelemetryPropertyValueType>;
}

export type ErrorEventProperties =
  | { [key: string]: never }
  | Pick<ApiErrorEventProperties, keyof ApiErrorEventProperties>
  | Pick<AuthAcquiredTokenExpiredErrorEventProperties, keyof AuthAcquiredTokenExpiredErrorEventProperties>
  | Pick<AuthAcquireTokenErrorEventProperties, keyof AuthAcquireTokenErrorEventProperties>
  | Pick<AuthAcquireTokenSilentErrorEventProperties, keyof AuthAcquireTokenSilentErrorEventProperties>
  | Pick<AuthBootstrapErrorEventProperties, keyof AuthBootstrapErrorEventProperties>
  | Pick<AuthHandleTokenInUrlFailedErrorEventProperties, keyof AuthHandleTokenInUrlFailedErrorEventProperties>
  | Pick<AuthUnsupportedScopeProvidedErrorEventProperties, keyof AuthUnsupportedScopeProvidedErrorEventProperties>
  | Pick<ContextualErrorEventProperties, keyof ContextualErrorEventProperties>
  | Pick<DeleteMessageErrorProperties, keyof DeleteMessageErrorProperties>
  | Pick<ErrorBoundaryErrorEventProperties, keyof ErrorBoundaryErrorEventProperties>
  | Pick<FeedLoadErrorEventProperties, keyof FeedLoadErrorEventProperties>
  | Pick<FileUploadErrorEventProperties, keyof FileUploadErrorEventProperties>
  | Pick<GenericErrorEventProperties, keyof GenericErrorEventProperties>
  | Pick<GraphApiErrorEventProperties, keyof GraphApiErrorEventProperties>
  | Pick<GroupErrorEventProperties, keyof GroupErrorEventProperties>
  | Pick<ImageLoadErrorProperties, keyof ImageLoadErrorProperties>
  | Pick<LivePersonaCardErrorEventProperties, keyof LivePersonaCardErrorEventProperties>
  | Pick<PostErrorEventProperties, keyof PostErrorEventProperties>
  | Pick<PropertyCheckEventProperties, keyof PropertyCheckEventProperties>
  | Pick<RealtimeErrorEventProperties, keyof RealtimeErrorEventProperties>
  | Pick<SearchResultThreadRenderErrorProperties, keyof SearchResultThreadRenderErrorProperties>
  | Pick<SharePointAuthErrorProperties, keyof SharePointAuthErrorProperties>
  | Pick<SurveyErrorProperties, keyof SurveyErrorProperties>
  | Pick<TeamsSdkMediaErrorEventProperties, keyof TeamsSdkMediaErrorEventProperties>
  | Pick<ThreadLoadErrorEventProperties, keyof ThreadLoadErrorEventProperties>
  | Pick<UnhandledErrorEventProperties, keyof UnhandledErrorEventProperties>
  | Pick<UnhandledRejectionEventProperties, keyof UnhandledRejectionEventProperties>;

export interface ApiErrorEventProperties {
  readonly apiName: string;
  readonly errorCode: ErrorCode;
  readonly requestType: string;
  readonly status: number;
}

export interface AuthAcquiredTokenExpiredErrorEventProperties {
  readonly endTime: number;
  readonly errorCode: ErrorCode.AuthAcquireTokenFailed;
  readonly expiration: number;
  readonly scopes: string;
  readonly startTime: number;
}

export interface AuthAcquireTokenErrorEventProperties {
  readonly errorCode: ErrorCode.AuthAcquireTokenFailed;
  readonly scopes: string;
}

export interface AuthAcquireTokenSilentErrorEventProperties {
  readonly authenticatorType: string;
  readonly errorCode: ErrorCode.AuthAcquireTokenSilentFailed;
  readonly scopes: string;
  readonly skipAuthentication: true;
}

export interface AuthBootstrapErrorEventProperties {
  readonly errorCode: ErrorCode.AuthBootstrapFailed;
  readonly isInTeamsApp?: boolean;
  readonly isExecutingInIFrame: boolean;
}

export interface AuthHandleTokenInUrlFailedErrorEventProperties {
  readonly authenticatorType: string;
  readonly errorCode: ErrorCode.AuthHandleTokenInUrlFailed;
  readonly skipAuthentication: true;
}

export interface AuthUnsupportedScopeProvidedErrorEventProperties {
  readonly authenticatorType: string;
  readonly errorCode: ErrorCode.AuthUnsupportedScopeProvided;
  readonly skipAuthentication: true;
}

export interface ContextualErrorEventProperties {
  readonly errorCode: ErrorCode.GetTeamsContextFailed;
  readonly contextMessage: string;
}

export interface DeleteMessageErrorProperties {
  readonly messageId: string;
}

export interface ErrorBoundaryErrorEventProperties {
  readonly errorBoundaryName: string;
  readonly errorCode: ErrorCode;
}

export interface FeedLoadErrorEventProperties {
  readonly actionName: string;
  readonly category: string;
  readonly subCategory?: string;
}

export interface FileUploadErrorEventProperties {
  readonly errorCode: ErrorCode.FileUploadFailed;
  readonly storageType: string;
}

export interface GenericErrorEventProperties {
  readonly errorCode: ErrorCode;
}

export interface GraphApiErrorEventProperties extends ApiErrorEventProperties {
  readonly requestId: string;
  readonly correlationId: string;
  readonly operationName: string;
  readonly reason: string;
}

export interface GroupErrorEventProperties {
  readonly errorCode: ErrorCode.GroupDeleted;
  readonly groupId: string;
}

export interface ImageLoadErrorProperties {
  readonly errorCode: ErrorCode.LoadImageFailed;
  readonly imageId: string;
  readonly imageOrigin: string;
  readonly imageSurface: string;
  readonly imageType: string;
  readonly isTenor: boolean;
  readonly isGif?: boolean;
}

export interface LivePersonaCardErrorEventProperties {
  readonly errorCode: ErrorCode;
  readonly logType: string;
  readonly logResultType: string;
  readonly contextMessage?: string;
}

export interface RealtimeErrorEventProperties {
  readonly channel?: string;
  readonly errorCode: ErrorCode.RealtimeError;
  readonly jitter?: number;
  readonly messageName?: string;
  readonly reason?: string;
  readonly disconnectCount?: number;
}

export interface SearchResultThreadRenderErrorProperties {
  readonly threadId: string;
}

export interface SharePointAuthErrorProperties {
  readonly correlationId: string;
  readonly name: string;
  readonly status: string;
}

export interface SurveyErrorProperties {
  readonly sessionId: string;
  readonly campaignId?: string;
}

export interface TeamsSdkMediaErrorEventProperties {
  readonly errorCode: ErrorCode.TeamsMediaApiFailed;
  readonly message?: string;
}

export interface ThreadLoadErrorEventProperties {
  readonly actionName: string;
}

export interface PostErrorEventProperties {
  readonly hasInlineStyle: boolean;
  readonly hasBlockStyle: boolean;
  readonly hasUserEntity: boolean;
  readonly hasLinkEntity: boolean;
  readonly hasTagEntity: boolean;
  readonly hasGroupEntity: boolean;
  readonly idempotencyToken: string;
  readonly isAnnouncement: boolean;
  readonly postType: string;
  readonly retryAttemptCount: number;
}

export interface UnhandledErrorEventProperties {
  readonly line: number;
  readonly col: number;
  readonly file: string;
}

export interface UnhandledRejectionEventProperties {
  readonly reason: string;
}

export enum ErrorCode {
  AcquireTokenTimedout = 'AcquireTokenTimedout',
  ApiError = 'ApiError',
  AnswersAnalyticsFailed = 'AnswersAnalyticsFailed',
  AudienceAnalyticsFailed = 'AudienceAnalyticsFailed',
  AuthAcquiredTokenExpired = 'AuthAcquiredTokenExpired',
  AuthAcquireTokenFailed = 'AuthAcquireTokenFailed',
  AuthAcquireTokenSilentFailed = 'AuthAcquireTokenSilentFailed',
  AuthBootstrapFailed = 'AuthBootstrapFailed',
  AuthenticatorLoadFailed = 'AuthenticatorLoadFailed',
  AuthHandleTokenInUrlFailed = 'AuthHandleTokenInUrlFailed',
  AuthTokenExpirationParsingFailed = 'AuthTokenExpirationParsingFailed',
  AuthUnsupportedScopeProvided = 'AuthUnsupportedScopeProvided',
  BootstrapFailed = 'BootstrapFailed',
  CampaignSummaryCardFailed = 'CampaignSummaryCardFailed',
  ClearImageRenderingCookieFailed = 'ClearImageRenderingCookieFailed',
  CommunityAnalyticsFailed = 'CommunityAnalyticsFailed',
  ContentStateBodyParseFailed = 'ContentStateBodyParseFailed',
  ContentStateMessagePostFailed = 'ContentStateMessagePostFailed',
  CreatePlaybackUrlFailed = 'CreatePlaybackUrlFailed',
  ErrorBoundary = 'ErrorBoundary',
  FeatureAccessSettingsFailed = 'FeatureAccessSettingsFailed',
  FileUploadFailed = 'FileUploadFailed',
  FloodgateInitializeError = 'FloodgateInitializeError',
  FloodgateStartError = 'FloodgateStartError',
  FloodgateInitializeErrorNoAadInfo = 'FloodgateInitializeErrorNoAadInfo',
  FloodgateSurveyError = 'FloodgateSurveyError',
  GetTeamsContextFailed = 'GetTeamsContextFailed',
  GetTeamsMeetingDetailsFailed = 'GetTeamsMeetingDetailsFailed',
  GetTeamsMeetingDetailsTimedout = 'GetTeamsMeetingDetailsTimedout',
  GraphqlApiError = 'GraphqlApiError',
  GroupDeleted = 'GroupDeleted',
  GroupEditAvatarFailed = 'GroupEditAvatarFailed',
  GroupPublisherFileUploadFailed = 'GroupPublisherFileUploadFailed',
  ImageFileEditDescriptionFailed = 'ImageFileEditDescriptionFailed',
  InitializeFeatureFlightingFailed = 'InitializeFeatureFlightingFailed',
  InitializeLivePersonaCardFailed = 'InitializeLivePersonaCardFailed',
  LeadershipThemeTopThreadsFailed = 'LeadershipThemeTopThreadsFailed',
  LeadershipThemeSummaryFailed = 'LeadershipThemeSummaryFailed',
  LiveTopicPickerAuthAcquireTokenFailed = 'LiveTopicPickerAuthAcquireTokenFailed',
  LivePersonaCardFailed = 'LivePersonaCardFailed',
  LoadAnimationDataFailed = 'LoadAnimationDataFailed',
  LoadCurrentUserFailed = 'LoadCurrentUserFailed',
  LoadDismissablePromptsFailed = 'LoadDismissablePromptsFailed',
  LoadImageFailed = 'LoadImageFailed',
  NetworkActivityFailed = 'NetworkActivityFailed',
  NetworkTopAnnouncementsFailed = 'NetworkTopAnnouncementsFailed',
  NetworkTopThreadsFailed = 'NetworkTopThreadsFailed',
  NetworkTopThreadStartersFailed = 'NetworkTopThreadStartersFailed',
  NetworkTopGroupsFailed = 'NetworkTopGroupsFailed',
  NetworkThemeTopThreadsFailed = 'NetworkThemeTopThreadsFailed',
  NetworkThemeSummaryFailed = 'NetworkThemeSummaryFailed',
  NetworkTopThemesFailed = 'NetworkTopThemesFailed',
  PerformanceApiUnavailable = 'PerformanceApiUnavailable',
  PerformHomeRedirectFailed = 'PerformHomeRedirectFailed',
  PersonalAnalyticsFailed = 'PersonalAnalyticsFailed',
  PreloadFeedFailed = 'PreloadFeedFailed',
  PreloadNavigationGroupsFailed = 'PreloadNavigationGroupsFailed',
  PreloadThreadFailed = 'PreloadThreadFailed',
  PushNotificationsNotificationFailed = 'PushNotificationsNotificationFailed',
  PushNotificationsSettingsErrorWhileSaving = 'PushNotificationsSettingsErrorWhileSaving',
  PWAInstallTeachingBubbleFailed = 'PWAInstallTeachingBubbleFailed',
  RealtimeError = 'RealtimeError',
  RealtimeHostLoadError = 'RealtimeHostLoadError',
  RepairTokenFailed = 'RepairTokenFailed',
  ServiceWorkerRegistrationFailed = 'ServiceWorkerRegistrationFailed',
  ServiceWorkerDisableFailed = 'ServiceWorkerDisableFailed',
  SetAppBadgeFailed = 'SetAppBadgeFailed',
  SetImageRenderingCookieFailed = 'SetImageRenderingCookieFailed',
  SharePointApiError = 'SharePointApiError',
  ShouldWriteCookieCheckFailed = 'ShouldWriteCookieCheckFailed',
  TeamsSearchFailed = 'TeamsSearchFailed',
  TeamsDeepLinkHandleFailed = 'TeamsDeepLinkHandleFailed',
  TeamsQnACachingRegistrationFailed = 'TeamsQnACachingRegistrationFailed',
  TeamsQnADeepLinkHandleFailed = 'TeamsQnADeepLinkHandleFailed',
  TeamsTabCachingRegistrationFailed = 'TeamsTabCachingRegistrationFailed',
  TeamsMediaApiFailed = 'TeamsMediaApiFailed',
  TopicsSDKFailed = 'TopicsSDKFailed',
  ThreadExtendedActivityFailed = 'ThreadExtendedActivityFailed',
  ThreadExtendedActivityTargetAudiencesAndNetworksFailed = 'ThreadExtendedActivityTargetAudiencesAndNetworksFailed',
  ThreadExtendedActivityTopRepliesFailed = 'ThreadExtendedActivityTopRepliesFailed',
  ThreadExtendedActivitySharesFailed = 'ThreadExtendedActivitySharesFailed',
  UnhandledError = 'UnhandledError',
  UnhandledRejection = 'UnhandledRejection',
  UpdateModernClientPreferenceFailed = 'UpdateModernClientPreferenceFailed',
  UpdateWebSessionHostFailed = 'UpdateWebSessionHostFailed',
  WebImageEditDescriptionFailed = 'WebImageEditDescriptionFailed',
  WriteHostingCookiesFailed = 'WriteHostingCookiesFailed',
}
